import React, { useState } from 'react';
import { defineMessages } from 'react-intl';
import { SaveOutlined, StopOutlined } from '@ant-design/icons';
import BaseModal from '@components/common/form/BaseModal';
import apiConfig from '@constants/apiConfig';
import { errorCodes } from '@constants/ErrorCode';
import useFetch from '@hooks/useFetch';
import useNotification from '@hooks/useNotification';
import useTranslate from '@hooks/useTranslate';
import { commonMessage } from '@locales/intl';
import PiServerDeviceForm from '@modules/digibesShop/Devices/tabs/piServer/PiServerDeviceForm';
import { showErrorMessage } from '@services/notifyService';
import { Button, Flex } from 'antd';

const message = defineMessages({
    saveSuccess: 'Save device successfully',
});

const FORM_ID = 'form-res-pi-server-device';
const EMPTY = {};

const usePiServerDeviceFormModal = ({ restaurantId, onReload } = {}) => {
    const translate = useTranslate();
    const notification = useNotification();

    const [ open, setOpen ] = useState(false);
    const [ detail, setDetail ] = useState(null);
    const [ isEditing, setIsEditing ] = useState(false);
    const [ changed, setChanged ] = useState(false);

    const { execute: executeGetDetail } = useFetch(apiConfig.kitplayDevice.getById, { immediate: false });
    const { execute: executeCreate, loading: loadingCreate } = useFetch(apiConfig.kitplayDevice.create, {
        immediate: false,
    });
    const { execute: executeUpdate, loading: loadingUpdate } = useFetch(apiConfig.kitplayDevice.update, {
        immediate: false,
    });

    const handleSaveError = (err) => {
        const errorCode = err?.response?.data?.code ?? err?.code;
        const errorMessage = errorCodes[errorCode]?.message;
        if (errorMessage) showErrorMessage(translate.formatMessage(errorMessage), translate);
        else showErrorMessage(err?.message || translate.formatMessage(commonMessage.error), translate);
    };

    const closeModal = () => {
        setOpen(false);
        setDetail(null);
        setChanged(false);
    };

    const openCreate = () => {
        setIsEditing(false);
        setDetail({});
        setChanged(false);
        setOpen(true);
    };

    const openEdit = (record) => {
        executeGetDetail({
            pathParams: { id: record.id },
            onCompleted: (res) => {
                setDetail(res?.result === true ? res.data : {});
                setIsEditing(true);
                setChanged(false);
                setOpen(true);
            },
            onError: () => notification({ type: 'error', message: translate.formatMessage(commonMessage.error) }),
        });
    };

    const onSuccess = () => {
        notification({ message: translate.formatMessage(message.saveSuccess) });
        closeModal();
        onReload?.();
    };

    const handleSubmit = (values) => {
        if (isEditing) {
            executeUpdate({
                data: { ...values, id: detail?.id, restaurantId },
                onCompleted: (res) => res?.result === true && onSuccess(),
                onError: handleSaveError,
            });
        } else {
            executeCreate({
                data: { ...values, restaurantId },
                onCompleted: (res) => res?.result === true && onSuccess(),
                onError: handleSaveError,
            });
        }
    };

    const renderModalFooter = (
        <Flex justify="end" gap={8} style={{ padding: '8px 16px' }}>
            <Button danger onClick={closeModal} icon={<StopOutlined />}>
                {translate.formatMessage(commonMessage.cancel)}
            </Button>
            <Button
                type="primary"
                htmlType="submit"
                form={FORM_ID}
                loading={loadingCreate || loadingUpdate}
                disabled={!changed}
                icon={<SaveOutlined />}
            >
                {isEditing
                    ? translate.formatMessage(commonMessage.update)
                    : translate.formatMessage(commonMessage.create)}
            </Button>
        </Flex>
    );

    const renderModals = () =>
        (
            <BaseModal
                isModalVisible={open}
                isEditing={isEditing}
                handleCancel={closeModal}
                title={translate.formatMessage(commonMessage.devices)}
                width="800px"
                noBodyPadding={true}
                customFooter={renderModalFooter}
            > {open && (
                    <PiServerDeviceForm
                        formId={FORM_ID}
                        dataDetail={detail || EMPTY}
                        isEditing={isEditing}
                        actions={null}
                        onSubmit={handleSubmit}
                        setIsChangedFormValues={setChanged}
                    />
                )}
            </BaseModal>
        );

    return { openCreate, openEdit, renderModals };
};

export default usePiServerDeviceFormModal;
